import {createStore} from 'zustand/vanilla';
import type {
  JourneyCustomAttribute,
  NodeProcessorMetadata,
} from '@gl/types/journeys.js';
import type {UUID} from '@gl/types/strings.js';

type GenericKey = UUID | string;
export type CustomAttributesRecord = Record<UUID, JourneyCustomAttribute>;
type NodeMetadataRecord = Record<GenericKey, NodeProcessorMetadata>;

export const DEFAULT_NODE_METADATA: NodeProcessorMetadata = {
  processing: false,
  metrics: null,
};

export type AttributeState = {
  nodeMetadata: NodeMetadataRecord;
  customAttributes: CustomAttributesRecord;

  setNodeMetadata: (
    key: GenericKey,
    metadata: Partial<NodeProcessorMetadata>
  ) => void;
  removeNodeMetadata: (key: GenericKey) => void;
  setCustomAttributes: (attributes: JourneyCustomAttribute[]) => void;
  addCustomAttribute: (attribute: JourneyCustomAttribute) => void;
  updateCustomAttribute: (
    id: UUID,
    updates: Partial<JourneyCustomAttribute>
  ) => void;
  removeCustomAttribute: (id: UUID) => void;
};

export const attributeStore = createStore<AttributeState>()(set => ({
  customAttributes: {},
  nodeMetadata: {},
  setNodeMetadata(key, metadata) {
    set(state => {
      state.nodeMetadata[key] = {
        ...(state.nodeMetadata[key] ?? DEFAULT_NODE_METADATA),
        ...metadata,
      };
      return state;
    });
  },
  removeNodeMetadata(key) {
    set(state => {
      if (state.nodeMetadata[key]) delete state.nodeMetadata[key];
      return state;
    });
  },
  setCustomAttributes: attributes => {
    const attributesRecord = attributes.reduce((acc, attr) => {
      acc[attr.id] = attr;
      return acc;
    }, {} as CustomAttributesRecord);
    set(state => ({
      ...state,
      customAttributes: attributesRecord,
    }));
  },
  addCustomAttribute: attribute =>
    set(state => ({
      ...state,
      customAttributes: {
        ...state.customAttributes,
        [attribute.id]: attribute,
      },
    })),
  updateCustomAttribute: (id, updates) =>
    set(state => ({
      customAttributes: {
        ...state.customAttributes,
        [id]: {
          ...state.customAttributes[id],
          ...updates,
        },
      },
    })),
  removeCustomAttribute: id =>
    set(state => {
      const newAttributes = {...state.customAttributes};
      delete newAttributes[id];
      return {
        ...state,
        customAttributes: newAttributes,
      };
    }),
}));

export function getCustomAttributesArray(state: AttributeState) {
  return Object.values(state.customAttributes);
}

export function findCustomAttribute(
  state: AttributeState,
  predicate: (attr: JourneyCustomAttribute) => boolean
) {
  return getCustomAttributesArray(state).find(predicate);
}

export function filterCustomAttributes(
  state: AttributeState,
  predicate: (attr: JourneyCustomAttribute) => boolean
) {
  return getCustomAttributesArray(state).filter(predicate);
}

export function addCustomAttributes(attributes: JourneyCustomAttribute[]) {
  attributes.forEach(attribute => {
    attributeStore.getState().addCustomAttribute(attribute);
  });
}
